import Grid2 from '@mui/material/Grid2';
import React from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import { UserCard } from 'molecules';
import useStyles from './styles';
import { reportError } from 'lib/errors';
import { QueryResult, StaffUser, StaffUsers } from '@elevator/ui';
import { LoadingError } from 'atoms';
import Skeleton from '@mui/material/Skeleton';

const RoleHeading = ({ children }: { children: React.ReactNode }) => (
  <Typography variant="subtitle1" align="center" width="100%">
    {children}
  </Typography>
);

/**
 * Staff Component
 *
 * Displays the ELEVATOR staff page with user cards organized by role.
 * Shows editors and developers in separate sections when at least one user exists for that role.
 *
 * Features:
 * - Role-based filtering (Editors, Engineers)
 * - User card grid display
 * - Loading and error states
 * - Responsive grid layout
 *
 * Used in:
 * - Staff layout index (rendered for /staff page)
 *
 * @param props - Component props
 * @param props.staffUsers - Array of staff user data
 * @param props.loading - Whether data is loading
 * @param props.loadingMore - Whether more data is being fetched
 * @param props.error - GraphQL query error
 * @param props.fetchMore - Function to fetch more staff users
 * @returns Staff component with user cards
 */
const Staff = (props: StaffTypes): React.JSX.Element => {
  const { classes } = useStyles();

  const { staffUsers, error, loading } = props;

  if (error) {
    return (
      <LoadingError error={error} failedTarget={'staff page'} reportError={reportError} />
    );
  }

  if (loading || !staffUsers) {
    return (
      <Box component="main" className={classes.container} data-layout="staff">
        <Box display="flex" justifyContent="center" width="100%">
          <Skeleton variant="text" width={200} height={60} />
        </Box>
        <Skeleton variant="rectangular" width="100%" height={2} sx={{ my: 2 }} />
        <Grid2 container spacing={2} justifyContent="center">
          {Array.from({ length: 6 }).map((_, index) => (
            <Grid2 key={index} size={{ xs: 12, sm: 6, md: 4 }}>
              <Box display="flex" justifyContent="center" width="100%">
                <Skeleton variant="rectangular" width="100%" height={300} />
              </Box>
            </Grid2>
          ))}
        </Grid2>
      </Box>
    );
  }

  const editors = staffUsers.filter((user) => user.role.includes('Editor'));
  const engineers = [...staffUsers.filter((user) => user.role === 'Engineer')].reverse();

  const renderUserCard = (user: StaffUser, key: string) => {
    return (
      <Grid2 key={key} size={{ xs: 12, sm: 6, md: 4 }}>
        <UserCard
          variant="fullWidth"
          user={user}
          name={`${user.firstName} ${user.lastName}`}
          socialMedia={user.socialMedia}
        />
      </Grid2>
    );
  };

  return (
    <Box component="main" className={classes.container} data-layout="staff">
      <Typography variant="h3" align="center" width="100%">
        STAFF
      </Typography>
      <Box className={classes.dividerContainer}>
        <Divider className={classes.divider} />
      </Box>
      <Stack spacing={4} width="100%" alignItems="center">
        {editors.length > 0 && (
          <Stack spacing={2} width="100%" alignItems="center">
            <RoleHeading>Editor-in-Chief</RoleHeading>
            <Grid2 container spacing={2} justifyContent="center" width="100%">
              {editors.map((editor) => renderUserCard(editor, editor.preferredUsername))}
            </Grid2>
          </Stack>
        )}
        {engineers.length > 0 && (
          <Stack spacing={2} width="100%" alignItems="center">
            <RoleHeading>Developers</RoleHeading>
            <Grid2 container spacing={2} justifyContent="center" width="100%">
              {engineers.map((engineer) =>
                renderUserCard(engineer, engineer.preferredUsername),
              )}
            </Grid2>
          </Stack>
        )}
      </Stack>
    </Box>
  );
};

export interface StaffTypes extends QueryResult<'staffUsers', StaffUsers> {}
export default Staff;
